/**
 * Admin-only platform landing content editor.
 *
 * Lets the platform admin live-edit every piece of copy on the public Landing
 * page (Hero / Features / How it works / Managed recruitment / CTA / Footer).
 *
 * Backed by GET/PATCH /api/settings/landing-content.
 *
 * Variable substitutions supported inside saved strings:
 *   {product_name}, {short_name}
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Save, RotateCcw, ExternalLink, ArrowLeft, Plus, Trash2 } from "lucide-react";

const Section = ({ title, children, hint }) => (
    <div className="border border-border bg-white p-5 sm:p-6 space-y-3">
        <div>
            <h3 className="font-display font-bold text-lg">{title}</h3>
            {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
        </div>
        {children}
    </div>
);

const Field = ({ label, value, onChange, multiline = false, placeholder, testid }) => (
    <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-widest font-mono text-foreground/70">{label}</Label>
        {multiline ? (
            <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="rounded-none min-h-[80px]" data-testid={testid} />
        ) : (
            <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="rounded-none h-10" data-testid={testid} />
        )}
    </div>
);

export default function AdminLandingContent() {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/settings/landing-content");
            setContent(data.content || {});
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const set = (path, value) => {
        setContent((c) => {
            const next = { ...c };
            const keys = path.split(".");
            let ref = next;
            for (let i = 0; i < keys.length - 1; i++) {
                ref[keys[i]] = { ...(ref[keys[i]] || {}) };
                ref = ref[keys[i]];
            }
            ref[keys[keys.length - 1]] = value;
            return next;
        });
    };

    const setListItem = (listKey, idx, field, value) => {
        setContent((c) => {
            const list = [...(c[listKey] || [])];
            list[idx] = { ...(list[idx] || {}), [field]: value };
            return { ...c, [listKey]: list };
        });
    };

    const setStringListItem = (listKey, idx, value) => {
        setContent((c) => {
            const list = [...(c[listKey] || [])];
            list[idx] = value;
            return { ...c, [listKey]: list };
        });
    };

    const addListItem = (listKey, template) => {
        setContent((c) => ({ ...c, [listKey]: [...(c[listKey] || []), template] }));
    };

    const removeListItem = (listKey, idx) => {
        setContent((c) => ({ ...c, [listKey]: (c[listKey] || []).filter((_, i) => i !== idx) }));
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/settings/landing-content", { content });
            toast.success("Landing page content saved");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const reset = async () => {
        if (!confirm("Reset all landing-page copy to platform defaults?")) return;
        setSaving(true);
        try {
            const { data } = await api.post("/settings/landing-content/reset");
            setContent(data.content || {});
            toast.success("Reset to defaults");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const hero = useMemo(() => content?.hero || {}, [content]);
    const features = content?.features || [];
    const howSteps = content?.how_steps || [];
    const managedBullets = content?.managed_bullets || [];

    if (loading || !content) return <div className="p-10 text-center text-sm text-muted-foreground">Loading landing content…</div>;

    return (
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-5" data-testid="admin-landing-content">
            {/* Sticky toolbar */}
            <div className="sticky top-0 z-20 -mx-3 sm:-mx-4 px-3 sm:px-4 py-3 bg-background/95 backdrop-blur border-b border-border flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                    <Link to="/admin" className="inline-flex items-center gap-1 text-xs font-mono text-muted-foreground hover:text-ikb" data-testid="back-to-admin">
                        <ArrowLeft className="w-3.5 h-3.5" /> Admin
                    </Link>
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest font-mono text-ikb">Platform · Landing page</div>
                        <h1 className="font-display font-black text-xl sm:text-2xl leading-tight truncate">Landing content</h1>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/" target="_blank" rel="noreferrer">
                        <Button variant="outline" className="rounded-none h-9" data-testid="preview-landing-btn">
                            <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Preview
                        </Button>
                    </Link>
                    <Button variant="outline" onClick={reset} disabled={saving} className="rounded-none h-9" data-testid="reset-defaults-btn">
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
                    </Button>
                    <Button onClick={save} disabled={saving} className="rounded-none h-9 bg-ikb hover:bg-[#001f7a] text-white" data-testid="save-landing-btn">
                        <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
                    </Button>
                </div>
            </div>

            <p className="text-xs text-muted-foreground">
                You can use the variables <code className="font-mono bg-secondary px-1">{"{product_name}"}</code> and{" "}
                <code className="font-mono bg-secondary px-1">{"{short_name}"}</code> inside any text — they'll be replaced with the current platform branding when the page renders.
            </p>

            {/* Hero */}
            <Section title="Hero" hint="Top of the landing page — the first thing visitors see.">
                <Field label="Badge (small text above headline)" value={hero.badge} onChange={(v) => set("hero.badge", v)} testid="hero-badge" />
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Headline line 1" value={hero.headline_line1} onChange={(v) => set("hero.headline_line1", v)} testid="hero-h1-1" />
                    <Field label="Headline line 2" value={hero.headline_line2} onChange={(v) => set("hero.headline_line2", v)} testid="hero-h1-2" />
                    <Field label="Headline line 3 (text before accent)" value={hero.headline_line3} onChange={(v) => set("hero.headline_line3", v)} testid="hero-h1-3" />
                    <Field label="Headline accent (highlighted word)" value={hero.headline_accent} onChange={(v) => set("hero.headline_accent", v)} testid="hero-h1-accent" />
                </div>
                <Field label="Subtext (paragraph below headline)" value={hero.subtext} onChange={(v) => set("hero.subtext", v)} multiline testid="hero-subtext" />
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Primary CTA button" value={hero.primary_cta} onChange={(v) => set("hero.primary_cta", v)} testid="hero-primary-cta" />
                    <Field label="Secondary CTA button" value={hero.secondary_cta} onChange={(v) => set("hero.secondary_cta", v)} testid="hero-secondary-cta" />
                </div>
                <div className="grid sm:grid-cols-3 gap-3 pt-1">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="space-y-2">
                            <div className="text-[10px] uppercase tracking-widest font-mono text-foreground/60">Stat {i}</div>
                            <Field label="Value" value={hero[`stat_${i}_value`]} onChange={(v) => set(`hero.stat_${i}_value`, v)} testid={`hero-stat-${i}-value`} />
                            <Field label="Label" value={hero[`stat_${i}_label`]} onChange={(v) => set(`hero.stat_${i}_label`, v)} testid={`hero-stat-${i}-label`} />
                        </div>
                    ))}
                </div>
            </Section>

            {/* Features */}
            <Section title="Features grid" hint="Capabilities section — supports up to 12 cards.">
                <Field label="Section heading" value={content.features_heading} onChange={(v) => set("features_heading", v)} testid="features-heading" />
                <div className="grid md:grid-cols-2 gap-3">
                    {features.map((f, i) => (
                        <div key={i} className="border border-border p-3 space-y-2 relative" data-testid={`feature-${i}`}>
                            <button onClick={() => removeListItem("features", i)} className="absolute top-2 right-2 text-rose-600 hover:text-rose-700" data-testid={`feature-del-${i}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                            <Field label={`Feature ${i + 1} title`} value={f.title} onChange={(v) => setListItem("features", i, "title", v)} testid={`feature-${i}-title`} />
                            <Field label="Body" value={f.body} onChange={(v) => setListItem("features", i, "body", v)} multiline testid={`feature-${i}-body`} />
                        </div>
                    ))}
                </div>
                {features.length < 12 && (
                    <Button variant="outline" onClick={() => addListItem("features", { title: "New feature", body: "Description…" })} className="rounded-none h-9" data-testid="feature-add-btn">
                        <Plus className="w-3.5 h-3.5 mr-1.5" /> Add feature
                    </Button>
                )}
            </Section>

            {/* How it works */}
            <Section title="How it works" hint="3-step workflow strip on the dark band.">
                <Field label="Section heading" value={content.how_heading} onChange={(v) => set("how_heading", v)} multiline testid="how-heading" />
                <div className="grid md:grid-cols-3 gap-3">
                    {howSteps.map((s, i) => (
                        <div key={i} className="border border-border p-3 space-y-2 relative" data-testid={`how-step-${i}`}>
                            <button onClick={() => removeListItem("how_steps", i)} className="absolute top-2 right-2 text-rose-600 hover:text-rose-700" data-testid={`how-del-${i}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                            <Field label="Step number" value={s.n} onChange={(v) => setListItem("how_steps", i, "n", v)} testid={`how-${i}-n`} />
                            <Field label="Title" value={s.title} onChange={(v) => setListItem("how_steps", i, "title", v)} testid={`how-${i}-title`} />
                            <Field label="Body" value={s.body} onChange={(v) => setListItem("how_steps", i, "body", v)} multiline testid={`how-${i}-body`} />
                        </div>
                    ))}
                </div>
                {howSteps.length < 6 && (
                    <Button variant="outline" onClick={() => addListItem("how_steps", { n: String(howSteps.length + 1).padStart(2, "0"), title: "New step", body: "Describe…" })} className="rounded-none h-9" data-testid="how-add-btn">
                        <Plus className="w-3.5 h-3.5 mr-1.5" /> Add step
                    </Button>
                )}
            </Section>

            {/* Live jobs heading */}
            <Section title="Live jobs heading">
                <Field label="Heading text" value={content.jobs_heading} onChange={(v) => set("jobs_heading", v)} testid="jobs-heading" />
            </Section>

            {/* Managed recruitment */}
            <Section title="Managed recruitment section" hint="The enquiry block right after the hero.">
                <Field label="Small label" value={content.managed_section_label} onChange={(v) => set("managed_section_label", v)} testid="managed-label" />
                <div className="grid sm:grid-cols-3 gap-3">
                    <Field label="Heading line 1" value={content.managed_heading_line1} onChange={(v) => set("managed_heading_line1", v)} testid="managed-h-1" />
                    <Field label="Heading accent" value={content.managed_heading_accent} onChange={(v) => set("managed_heading_accent", v)} testid="managed-h-accent" />
                    <Field label="Heading line 2" value={content.managed_heading_line2} onChange={(v) => set("managed_heading_line2", v)} testid="managed-h-2" />
                </div>
                <Field label="Paragraph" value={content.managed_paragraph} onChange={(v) => set("managed_paragraph", v)} multiline testid="managed-para" />
                <Field label="Form title" value={content.managed_form_title} onChange={(v) => set("managed_form_title", v)} testid="managed-form-title" />
                <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest font-mono text-foreground/70">Bullets</Label>
                    {managedBullets.map((b, i) => (
                        <div key={i} className="flex gap-2">
                            <Input value={b} onChange={(e) => setStringListItem("managed_bullets", i, e.target.value)} className="rounded-none h-9" data-testid={`managed-bullet-${i}`} />
                            <Button variant="outline" size="icon" onClick={() => removeListItem("managed_bullets", i)} className="rounded-none h-9 w-9 shrink-0" data-testid={`managed-bullet-del-${i}`}>
                                <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                            </Button>
                        </div>
                    ))}
                    {managedBullets.length < 12 && (
                        <Button variant="outline" onClick={() => addListItem("managed_bullets", "New bullet")} className="rounded-none h-8 text-xs" data-testid="managed-bullet-add">
                            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add bullet
                        </Button>
                    )}
                </div>
            </Section>

            {/* CTA */}
            <Section title="Bottom CTA">
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Heading line 1" value={content.cta_headline_line1} onChange={(v) => set("cta_headline_line1", v)} testid="cta-h-1" />
                    <Field label="Heading line 2 (text before accent)" value={content.cta_headline_line2} onChange={(v) => set("cta_headline_line2", v)} testid="cta-h-2" />
                    <Field label="Heading accent" value={content.cta_headline_accent} onChange={(v) => set("cta_headline_accent", v)} testid="cta-h-accent" />
                    <Field label="Heading line 3 (after accent)" value={content.cta_headline_line3} onChange={(v) => set("cta_headline_line3", v)} testid="cta-h-3" />
                </div>
                <Field label="Subtext" value={content.cta_subtext} onChange={(v) => set("cta_subtext", v)} multiline testid="cta-subtext" />
                <Field label="Button label" value={content.cta_button} onChange={(v) => set("cta_button", v)} testid="cta-button" />
            </Section>

            {/* Footer */}
            <Section title="Footer">
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Copyright line" value={content.footer_copyright} onChange={(v) => set("footer_copyright", v)} testid="footer-copy" />
                    <Field label="Tagline" value={content.footer_tagline} onChange={(v) => set("footer_tagline", v)} testid="footer-tagline" />
                </div>
            </Section>

            <div className="flex justify-end pt-2 pb-12">
                <Button onClick={save} disabled={saving} className="rounded-none h-10 bg-ikb hover:bg-[#001f7a] text-white" data-testid="save-landing-btn-bottom">
                    <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
                </Button>
            </div>
        </div>
    );
}
